<script lang="ts">
  import type { FormField } from '../types';
  import { resetField } from '$lib/helpers/form-field-reset';
  import countryDetectionHelper from '$lib/helpers/country-detection-helper';

  import USFlag from 'public-web-icons/dist/flags/US.png';
  import GBFlag from 'public-web-icons/dist/flags/GB.png';
  import AUFlag from 'public-web-icons/dist/flags/AU.png';
  import CAFlag from 'public-web-icons/dist/flags/CA.png';
  import JPFlag from 'public-web-icons/dist/flags/JP.png';
  import IEFlag from 'public-web-icons/dist/flags/IE.png';
  import ESFlag from 'public-web-icons/dist/flags/ES.png';
  import FRFlag from 'public-web-icons/dist/flags/FR.png';

  export let localizedCountryNames: Record<string, string>;

  const countriesObject: Record<string, any> = {
    US: {
      countryCode: 'US',
      countryName: localizedCountryNames['us'],
      flag: USFlag,
      countryNumberPlaceholder: '000 000 0000',
      countryNumberCode: '+1',
    },
    GB: {
      countryCode: 'GB',
      countryName: localizedCountryNames['gb'],
      flag: GBFlag,
      countryNumberPlaceholder: '0000 000000',
      countryNumberCode: '+44',
    },
    AU: {
      countryCode: 'AU',
      countryName: localizedCountryNames['au'],
      flag: AUFlag,
      countryNumberPlaceholder: '0000-000-000',
      countryNumberCode: '+61',
    },
    CA: {
      countryCode: 'CA',
      countryName: localizedCountryNames['ca'],
      flag: CAFlag,
      countryNumberPlaceholder: '000 000 0000',
      countryNumberCode: '+1',
    },
    JP: {
      countryCode: 'JP',
      countryName: localizedCountryNames['jp'],
      flag: JPFlag,
      countryNumberPlaceholder: '00 0000 0000',
      countryNumberCode: '+81',
    },
    IE: {
      countryCode: 'IE',
      countryName: localizedCountryNames['ie'],
      flag: IEFlag,
      countryNumberPlaceholder: '0000000000',
      countryNumberCode: '+353',
    },
    ES: {
      countryCode: 'ES',
      countryName: localizedCountryNames['es'],
      flag: ESFlag,
      countryNumberPlaceholder: '000000000',
      countryNumberCode: '+34',
    },
    FR: {
      countryCode: 'FR',
      countryName: localizedCountryNames['fr'],
      flag: FRFlag,
      countryNumberPlaceholder: '00 00 00 00 00',
      countryNumberCode: '+33',
    },
  };

  export let field: FormField;
  export let inputPhoneNumberDigitsOnly: string;
  export let isError: boolean = false;

  export let locale: string = '';

  const defaultLocale = countryDetectionHelper.extractCountryCodeFromLocale(locale);

  let selectDropdown: HTMLDivElement;
  let rawPhoneNumberInput: HTMLInputElement;

  let selectedCountry = countriesObject[defaultLocale.toUpperCase()] || countriesObject.US;
  let isOpen: boolean = false;
  let inputPhoneNumber = field.autofillValue || '';

  $: inputPhoneNumberDigitsOnly = inputPhoneNumber.replaceAll(/\D+/g, '');
  $: fullPhoneNumber = selectedCountry.countryNumberCode + inputPhoneNumberDigitsOnly;

  function toggleDropdown() {
    isOpen ? closeDropdown() : openDropdown();
  }

  function openDropdown() {
    isOpen = true;
    document.body.addEventListener('click', closeDropdown);
  }

  function closeDropdown() {
    isOpen = false;
    document.body.removeEventListener('click', closeDropdown);
  }

  function setCountry(countryCode: string | undefined) {
    if (countryCode) {
      selectedCountry = countriesObject[countryCode];
    }
  }

  /* eslint-disable no-fallthrough */
  function handleInputTriggerKeydown(e: KeyboardEvent & { currentTarget: EventTarget & HTMLButtonElement }): any {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        e.preventDefault();
        selectDropdown.querySelector<HTMLElement>(`#prefixFor${selectedCountry.countryCode}`)?.focus();
        openDropdown();
        break;
      }
      case 'Escape': {
        e.preventDefault();
      }
      case 'Tab': {
        closeDropdown();
      }
    }
  }

  function handleDropdownKeydown(e: KeyboardEvent & { currentTarget: EventTarget & HTMLDivElement }): any {
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        (document.activeElement?.nextElementSibling as HTMLElement)?.focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        (document.activeElement?.previousElementSibling as HTMLElement)?.focus();
        break;
      }
      case 'Enter':
      case ' ': {
        setCountry(document.activeElement?.id.slice(-2));
      }
      case 'Escape': {
        e.preventDefault();
        rawPhoneNumberInput.focus();
      }
      case 'Tab': {
        closeDropdown();
        break;
      }
    }
  }
  /* eslint-enable no-fallthrough */
</script>

<fieldset class:isError>
  {#if field.label}
    <legend>{field.label}</legend>
  {/if}
  <div class="phoneFieldWrapper">
    <button
      type="button"
      class="fs-exclude countryCodeTrigger"
      aria-expanded={isOpen}
      on:click|stopPropagation={toggleDropdown}
      on:keydown={handleInputTriggerKeydown}
    >
      <span class="selectedFlag flagIcon">
        <img src={selectedCountry.flag} alt={selectedCountry.countryName} loading="lazy" />
      </span>
      <span class="arrowIcon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" width="14" height="14">
          <path d="M1 5l6 6 6-6" fill="none" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </span>
    </button>

    <div
      class="intlSelectDropdown"
      role="radiogroup"
      on:keydown={handleDropdownKeydown}
      bind:this={selectDropdown}
      tabindex="0"
    >
      <div class="intlSelectScroll">
        {#each Object.entries(countriesObject) as [countryCode, countryObject]}
          <!-- this is fine because the outer div handles using the keyboard to navigate dropdown -->
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div
            class="fs-exclude intlSelectOption"
            id="prefixFor{countryCode}"
            role="radio"
            tabindex="-1"
            aria-checked={selectedCountry.countryCode === countryCode}
            on:click={() => setCountry(countryCode)}
          >
            <label for="prefixFor{countryCode}" id="labelFor{countryCode}">
              <span class="flagIcon">
                <!-- don't need alt text, screen reader sees the label -->
                <img src={countryObject.flag} alt="" loading="lazy" />
              </span>
              <span class="countryOptionLabel">{countryObject.countryName}</span>
              <span class="countryOptionCode">{countryObject.countryNumberCode}</span>
            </label>
          </div>
        {/each}
      </div>
    </div>

    <input
      bind:this={rawPhoneNumberInput}
      bind:value={inputPhoneNumber}
      class="fs-exclude phoneNumberInput"
      type="tel"
      placeholder={selectedCountry.countryNumberPlaceholder}
      autocomplete={field.autocompleteType}
      title={field.validationMessage}
      required={field.required}
      use:resetField
    />

    <!--Actual input that holds the full number-->
    <input bind:value={fullPhoneNumber} class="fs-exclude" type="hidden" id={field.id} name={field.id} />
  </div>
</fieldset>

<style lang="scss">
  fieldset {
    .countryCodeTrigger {
      cursor: pointer;
      outline: none;
      padding: 13px 20px;
      background-color: transparent;
      border: none;
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      padding-right: var(--space-10);

      .arrowIcon {
        flex-shrink: 0;
        width: 14px;
        line-height: 1;
        display: block;
        stroke: var(--color-gray-55);
      }
    }

    .intlSelectDropdown {
      z-index: 200;
      position: absolute;
      top: calc(100% - 4px);
      width: calc(100% - 20px);
      background-color: white;
      opacity: 0;
      visibility: hidden;
      overflow: hidden;
      border-radius: 0 0 5px 5px;
      transition:
        opacity 0.3s cubic-bezier(0, 0, 0.2, 1),
        visibility 0.3s cubic-bezier(0, 0, 0.2, 1);

      &::after {
        content: '';
        pointer-events: none;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        box-shadow: inset 0 0 0 2px var(--color-blue);
        border-radius: 0 0 5px 5px;
      }
    }

    [aria-expanded='true'] + .intlSelectDropdown {
      opacity: 1;
      visibility: visible;
    }

    .intlSelectScroll {
      overflow-y: scroll;
      max-height: 182px;
      background-repeat: no-repeat;
      background-position:
        top center,
        bottom center,
        top center,
        bottom center;
      background-attachment: local, local, scroll, scroll;
      background-image:
        linear-gradient(to bottom, white 1em, rgba(255, 255, 255, 0) 2em),
        linear-gradient(to top, white 1em, rgba(255, 255, 255, 0) 2em),
        linear-gradient(to bottom, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0) 1em),
        linear-gradient(to top, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0) 1em);
    }

    .intlSelectOption {
      box-shadow: inset 0 0 0 1px var(--color-blue);
      padding: 12px 20px;
      &:focus {
        outline: none;
        background-color: var(--color-accent-transparent);
      }

      .countryOptionLabel {
        font-weight: var(--font-weight-light);
        color: var(--color-input-text);
      }

      .countryOptionCode {
        font-weight: var(--font-weight-light);
        color: var(--color-gray-30);
      }
    }

    .phoneFieldWrapper {
      display: flex;
      overflow: hidden;
      width: 100%;
      height: 50px;
      font-size: var(--font-size-16);
      line-height: var(--line-height-28);
      font-weight: var(--font-weight-base);
      color: var(--color-input-text);
      background-color: var(--color-white);
      border-radius: 5px;

      box-shadow: inset 0 0 0 1px var(--color-gray-55);

      transition:
        box-shadow var(--duration-quick) var(--easing-sharp),
        background-color var(--duration-quick) var(--easing-sharp),
        opacity var(--duration-quick) var(--easing-sharp);

      &:focus-within {
        box-shadow: inset 0 0 0 2px var(--color-blue);
      }
    }

    ::placeholder {
      color: var(--color-gray-30);
    }

    .phoneNumberInput {
      width: 100%;
      padding: calc((50px - var(--line-height-28)) / 2) calc((50px - var(--font-size-16)) / 2)
        calc((50px - var(--line-height-28)) / 2) 0;
    }

    .flagIcon {
      display: inline-block;
      height: 18px;
      width: 32px;
      margin-right: var(--space-10);
      vertical-align: middle;
      background-color: var(--color-accent);
      outline: 1px solid rgba(128, 128, 128, 0.25);
    }

    &.isError {
      .phoneFieldWrapper {
        box-shadow: inset 0 0 0 1px var(--color-red);
        // TODO: Use SVG
        background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8ZyBjbGlwLXBhdGg9InVybCgjdHJpYW5nbGUtd2FybmluZy1yZWRfc3ZnX19jbGlwMCkiIGZpbGw9IiNkZjMzMjAiPgogICAgPHBhdGggZD0iTTIyLjgxMyAxOS4xMDVsLS44Ni41MDkuMDA4LjAxMy44NTItLjUyMnpNMTMuMDY1IDIuNjE2bC44Ni0uNTA5YTMuOTk1IDMuOTk1IDAgMCAwLS4wMDgtLjAxM2wtLjg1Mi41MjJ6bS0yLjEzOSAwbC44NjEuNTA5YTEuMDMgMS4wMyAwIDAgMCAuMDItLjAzN2wtLjg4LS40NzJ6TTEuMTc4IDE5LjEwNWwuODUzLjUyMmEuNjguNjggMCAwIDAgLjAwOC0uMDEzbC0uODYxLS41MXpNMTEgMTNhMSAxIDAgMSAwIDIgMGgtMnptMi00YTEgMSAwIDEgMC0yIDBoMnpNMi4yNDcgMjJoMTkuNDk3di0ySDIuMjQ3djJ6bTE5LjQ5NyAwYzEuODA3IDAgMi44MjMtMS45NDcgMS45MjItMy40MThsLTEuNzA2IDEuMDQ1YS4yNC4yNCAwIDAgMSAuMDA5LjI1LjIzMi4yMzIgMCAwIDEtLjA4Mi4wODguMjY2LjI2NiAwIDAgMS0uMTQzLjAzNXYyem0xLjkzLTMuNDA0TDEzLjkyNiAyLjEwN2wtMS43MjIgMS4wMTggOS43NDggMTYuNDg5IDEuNzIyLTEuMDE4ek0xMy45MTggMi4wOTRjLS44NjYtMS40MTQtMy4wMzItMS41Mi0zLjg3My4wNWwxLjc2My45NDRhLjE0OC4xNDggMCAwIDEgLjA2LS4wNjMuMjE1LjIxNSAwIDAgMSAuMTA0LS4wMjVjLjA5MSAwIC4xODIuMDQzLjI0LjEzOGwxLjcwNi0xLjA0NHptLTMuODUyLjAxM0wuMzE3IDE4LjU5NmwxLjcyMiAxLjAxOCA5Ljc0OC0xNi40OS0xLjcyMS0xLjAxN3pNLjMyNiAxOC41ODJDLS42IDIwLjA5MS41NDUgMjIgMi4yNDYgMjJ2LTJjLS4wNzEgMC0uMTUzLS4wMzgtLjIwOS0uMTM2LS4wNTMtLjA5NC0uMDQ1LS4xNzUtLjAwNy0uMjM3TC4zMjUgMTguNTgyek0xMyAxM1Y5aC0ydjRoMnoiLz4KICAgIDxjaXJjbGUgY3g9IjEyIiBjeT0iMTciIHI9IjEiLz4KICA8L2c+CiAgPGRlZnM+CiAgICA8Y2xpcFBhdGggaWQ9InRyaWFuZ2xlLXdhcm5pbmctcmVkX3N2Z19fY2xpcDAiPgogICAgICA8cGF0aCBmaWxsPSIjZmZmIiBkPSJNMCAwaDI0djI0SDB6Ii8+CiAgICA8L2NsaXBQYXRoPgogIDwvZGVmcz4KPC9zdmc+Cg==');
        background-position: calc(100% - 17px) 13px;
        background-repeat: no-repeat;

        &:focus-within {
          box-shadow: inset 0 0 0 2px var(--color-red);
        }
      }
      legend {
        color: var(--color-red);
      }
    }
  }
</style>
